import { type LatLng } from './geo';

// Mapbox Static Images URL builder for the "you ↔ polling unit" snapshot.
// A single <img> is the whole map on the step: no GL runtime, no tiles, no
// WebGL requirement — one request that a low-end phone on 2G can afford.

export type MapTheme = 'light' | 'dark';

export interface StaticMapParams {
	user: LatLng;
	pu: LatLng;
	theme: MapTheme;
	/** CSS pixel width of the rendered image (clamped to Mapbox's 1280 limit). */
	width: number;
	/** CSS pixel height of the rendered image (clamped to Mapbox's 1280 limit). */
	height: number;
	/** Request a 2× raster for high-density screens. */
	retina: boolean;
	/** Six-digit hex WITHOUT '#', used for the path and the unit pin. */
	brandHex: string;
	/** Six-digit hex WITHOUT '#' for the applicant's pin; defaults to `brandHex`. */
	userHex?: string;
	token: string;
}

export const MAPBOX_STATIC_BASE = 'https://api.mapbox.com/styles/v1/mapbox';
export const MAX_STATIC_DIMENSION = 1280;
export const STATIC_MAP_PADDING = 40;

// "Community Green" primary — the light-mode value of `--color-brand-primary`.
export const FALLBACK_BRAND_HEX = '1e5631';
// Deep-green ink — the light-mode value of `--color-text-primary`.
export const FALLBACK_INK_HEX = '1c2a20';

// Vite inlines `process.env.REACT_APP_*` only for keys present in the env files;
// without the key the expression stays as-is and `process` is undefined in the
// browser, hence the try/catch. The name is committed with an EMPTY value — a
// real public token belongs in `.env.local` / the build environment only.
const readMapboxToken = (): string | undefined => {
	try {
		const value = process.env.REACT_APP_MAPBOX_TOKEN;
		const trimmed = (value ?? '').trim();
		return trimmed ? trimmed : undefined;
	} catch {
		return undefined;
	}
};

export const MAPBOX_TOKEN: string | undefined = readMapboxToken();

export const mapboxStyleId = (theme: MapTheme): string => (theme === 'dark' ? 'dark-v11' : 'light-v11');

const HEX_SIX = /^#?([0-9a-f]{6})$/i;
const HEX_THREE = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;

/**
 * Normalises a CSS hex colour to six lowercase digits without '#', or null
 * when the value is not a hex colour at all (e.g. `rgb(...)`, a var() alias).
 */
export const normaliseHex = (value: string | null | undefined): string | null => {
	const trimmed = (value ?? '').trim();
	const six = HEX_SIX.exec(trimmed);
	if (six) return six[1].toLowerCase();
	const three = HEX_THREE.exec(trimmed);
	if (three) return `${three[1]}${three[1]}${three[2]}${three[2]}${three[3]}${three[3]}`.toLowerCase();
	return null;
};

/**
 * Reads a colour token off the document root at call time so the map matches
 * the party-themed brand (and the active light/dark palette). Falls back to the
 * iElect default when the token is missing or not a plain hex.
 */
export const readTokenHex = (tokenName: string, fallbackHex: string): string => {
	try {
		if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return fallbackHex;
		const value = getComputedStyle(document.documentElement).getPropertyValue(tokenName);
		return normaliseHex(value) ?? fallbackHex;
	} catch {
		return fallbackHex;
	}
};

export const readBrandHex = (): string => readTokenHex('--color-brand-primary', FALLBACK_BRAND_HEX);
export const readInkHex = (): string => readTokenHex('--color-text-primary', FALLBACK_INK_HEX);

interface NetworkInformationLike {
	saveData?: boolean;
	effectiveType?: string;
}

/**
 * True when the browser signals Data Saver or a 2G-class connection. Used to
 * skip the `@2x` raster — half the bytes for a snapshot nobody will pinch-zoom.
 */
export const prefersLightweightMap = (): boolean => {
	try {
		const nav = navigator as Navigator & { connection?: NetworkInformationLike };
		const connection = nav.connection;
		if (!connection) return false;
		if (connection.saveData === true) return true;
		const type = (connection.effectiveType ?? '').toLowerCase();
		return type === '2g' || type === 'slow-2g';
	} catch {
		return false;
	}
};

const clampDimension = (value: number): number => {
	if (!Number.isFinite(value)) return 1;
	return Math.min(MAX_STATIC_DIMENSION, Math.max(1, Math.round(value)));
};

const fixed5 = (value: number): string => value.toFixed(5);

// Google's encoded-polyline algorithm (precision 5), which is what the Static
// Images `path` overlay expects. Two points only here, but kept general.
const encodeSigned = (value: number): string => {
	let v = value < 0 ? ~(value << 1) : value << 1;
	let out = '';
	while (v >= 0x20) {
		out += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
		v >>= 5;
	}
	out += String.fromCharCode(v + 63);
	return out;
};

export const encodePolyline = (points: LatLng[]): string => {
	let lastLat = 0;
	let lastLng = 0;
	let out = '';
	points.forEach((point) => {
		const lat = Math.round(point.lat * 1e5);
		const lng = Math.round(point.lng * 1e5);
		out += encodeSigned(lat - lastLat) + encodeSigned(lng - lastLng);
		lastLat = lat;
		lastLng = lng;
	});
	return out;
};

/**
 * Builds the Static Images request:
 *
 *   {base}/{light|dark}-v11/static/
 *     path-3+{hex}-0.8({encoded polyline user→unit}),
 *     pin-s-v+{hex}({lng},{lat}),     ← the polling unit (v)
 *     pin-s-u+{userHex}({lng},{lat})  ← the applicant (u)
 *   /auto/{w}x{h}{@2x}?padding=40&access_token=…
 *
 * Pins take **lng,lat** (GeoJSON order). `auto` frames both pins with padding.
 */
export const buildStaticMapUrl = ({
	user,
	pu,
	theme,
	width,
	height,
	retina,
	brandHex,
	userHex,
	token,
}: StaticMapParams): string => {
	const pathHex = normaliseHex(brandHex) ?? FALLBACK_BRAND_HEX;
	const unitHex = pathHex;
	const applicantHex = normaliseHex(userHex) ?? pathHex;

	const path = `path-3+${pathHex}-0.8(${encodeURIComponent(encodePolyline([user, pu]))})`;
	const unitPin = `pin-s-v+${unitHex}(${fixed5(pu.lng)},${fixed5(pu.lat)})`;
	const userPin = `pin-s-u+${applicantHex}(${fixed5(user.lng)},${fixed5(user.lat)})`;

	const size = `${clampDimension(width)}x${clampDimension(height)}${retina ? '@2x' : ''}`;

	return `${MAPBOX_STATIC_BASE}/${mapboxStyleId(theme)}/static/${path},${unitPin},${userPin}/auto/${size}?padding=${STATIC_MAP_PADDING}&access_token=${encodeURIComponent(token)}`;
};

/** Mapbox Static Tiles template for Leaflet (512 px tiles, `zoomOffset: -1`). */
export const MAPBOX_TILE_URL = 'https://api.mapbox.com/styles/v1/mapbox/{id}/tiles/512/{z}/{x}/{y}@2x?access_token={accessToken}';
export const MAPBOX_TILE_ATTRIBUTION = '© Mapbox © OpenStreetMap';

/** The tile template with the token substituted (`{id}`, `{z}`, `{x}`, `{y}` stay for Leaflet). */
export const mapboxTileUrl = (token: string): string => MAPBOX_TILE_URL.replace('{accessToken}', encodeURIComponent(token));
